/**
 * Step adapter — bridges NS solver to the simulation loop.
 *
 * For predefined BoP models: uses dedicated NS pipe networks (bopNetworks.ts)
 * with proper pipe dimensions, pressures, and mass flows.
 *
 * For user-built systems: auto-generates NS network from canvas components.
 */

import type { Node, Edge } from 'reactflow';
import type { SimulationState, Site, SimEvent } from '../../types';
import type { NSSolverState, PipeSegment } from '../types';
import { DEFAULT_SIMPLE_CONFIG } from '../types';
import { buildNSNetwork } from './networkBuilder';
import { UnsupportedCustomComponentExecutionError } from '../components/nsCustom';
import { NSPipeInputAdmissionError } from '../core/inputAdmission';
import {
  networkSimpleStep,
  networkMaxTimeStep,
  computeEnergyClosure,
  deriveSolverConfig,
  TWO_PHASE_CAP_WARNING,
} from '../network/nsNetwork';
import { syncNSToNodes, syncNSToEdges } from './stateSync';
import { createBop101NS, createBop102NS, createBop103NS, createBop104NS } from '../networks/bopNetworks';
import { createCoolingNetworkNS, updateHXCoupling, DEFAULT_COOLING_CONFIG } from '../networks/coolingNetwork';
import {
  acousticStep,
  createAcousticState,
  removeAcousticPerturbations,
  pipeMeanAcousticProps,
  kortewegWaveSpeed,
  type AcousticState,
  type AcousticBoundarySpec,
} from '../solver/acousticSubstep';
import { vaporPressureFromT_SI } from '../core/fluidAdapter';

// ── W7 water-hammer wiring (INTEGRATION_PATCHES.W7 P1/P2) ───────────────────
// Acoustic overlay states, keyed by solver-state identity (main-thread
// playback only — the worker deserializes a NEW state object, so overlays do
// not survive a worker round-trip; AcousticState is structured-clone-safe if
// worker-side acoustics are wanted later). The workerClient guards this by
// forcing the main-thread path while acoustics are enabled (R1-A3 M4).
const acousticOverlays = new WeakMap<object, Map<string, AcousticState>>();

/** Per-solver-state acoustic overlay map (valve-closure detection populates it). */
export function getAcousticOverlays(state: object): Map<string, AcousticState> {
  let m = acousticOverlays.get(state);
  if (!m) { m = new Map(); acousticOverlays.set(state, m); }
  return m;
}

/**
 * Valve opening thresholds for the acoustic event detector (fractions, 0–1).
 * CLOSE fires on a drop from above VALVE_REOPEN_THRESHOLD to at/below
 * VALVE_CLOSED_THRESHOLD in one frame ("rapid full closure", unchanged from
 * the original W7 wiring: 0.05 → 0.01). REOPEN fires when the opening rises
 * back past VALVE_REOPEN_THRESHOLD (W7 caveat 5).
 */
export const VALVE_CLOSED_THRESHOLD = 0.01;
export const VALVE_REOPEN_THRESHOLD = 0.05;

/**
 * W7 caveat 2 — column-separation (cavitation) warning text. Exported as a
 * builder so tests pin the exact user-facing string. Values are reported in
 * kPa; pMin may be negative (the linear overlay does not model cavitation).
 */
export function columnSeparationWarning(pipeId: string, pMinPa: number, pSatPa: number): string {
  return (
    `Column separation likely at ${pipeId}: predicted minimum pressure ` +
    `${(pMinPa / 1e3).toFixed(1)} kPa < Psat ${(pSatPa / 1e3).toFixed(2)} kPa — ` +
    `water-hammer results invalid past the first downsurge.`
  );
}

// P2 — valve-closure event detection: a rapid opening drop on any valve arms a
// 'closed' acoustic boundary on the pipe feeding it (the Joukowsky wave source
// ρ·c·Δu). MUST run BEFORE networkSimpleStep each frame: createAcousticState
// samples the base velocity at creation, i.e. the PRE-closure flow.
//
// R1-A3 (this wave) closes W7 caveats #2/#5/#6 at the arm/lifecycle seam:
//  • caveat 5 — overlays arm WITH linearized steady friction ({friction:true})
//    so standing waves decay physically instead of ringing forever, and a
//    reopen strips the pipe's overlay AFTER restoring the base FV state.
//  • caveat 2 — at arm time the frictionless Joukowsky downsurge bound
//    P_min = P0 − ρ·c·Δu at the closure face is checked against p_sat(T_local)
//    (IAPWS saturation line via the existing fluid adapter); a breach surfaces
//    a column-separation warning. WARNING ONLY — the overlay physics is never
//    altered by the check (friction makes the real downsurge slightly smaller,
//    so the frictionless bound is conservative).
//  • caveat 6 — when the pipe carries wall data (wallThickness, set by the
//    cooling factory's schedule-40 sizing), the overlay's wave speed is the
//    Korteweg elastic-wall value c_K = c/√(1 + (K/E)·(D/e)); pipes without
//    wall data keep the rigid-pipe fluid sound speed exactly as before.
const lastOpening = new WeakMap<object, Map<string, number>>();

/** Pipes feeding a component (inflow connections) with the pipe end that sits at the component. */
function feedPipesOf(state: NSSolverState, componentId: string): Array<{ pipe: PipeSegment; end: 'left' | 'right' }> {
  const out: Array<{ pipe: PipeSegment; end: 'left' | 'right' }> = [];
  for (const jn of state.junctions.values()) {
    if (jn.componentId !== componentId) continue;
    const conn = jn.connections.find((c) => c.direction === 'inflow'); // pipe feeding the valve
    const pipe = conn ? state.pipes.get(conn.pipeId) : undefined;
    if (!pipe) continue;
    out.push({ pipe, end: conn!.faceIndex === 0 ? 'left' : 'right' });
  }
  return out;
}

/**
 * Arm one closure overlay on `pipe` (closed at `end`, reservoir opposite).
 * Any failure is surfaced as a warning instead of aborting the whole frame —
 * a diagnostic overlay must never take the base solve down with it.
 */
function armClosureOverlay(
  state: NSSolverState,
  pipe: PipeSegment,
  end: 'left' | 'right',
  warnings?: string[],
): void {
  const overlays = getAcousticOverlays(state);
  try {
    // Lifecycle hygiene: if an overlay is being REPLACED, strip what it has
    // applied to the FV cells first — otherwise the superseded wave stays
    // baked into the base state forever (exact restore via applied-delta
    // bookkeeping; see removeAcousticPerturbations).
    const previous = overlays.get(pipe.id);
    if (previous) removeAcousticPerturbations(previous, pipe);

    // W7 caveat 6 — Korteweg elastic-wall wave speed when wall data exists.
    // Means come from the SAME helper createAcousticState freezes its c/ρ
    // from, so the correction is consistent with the overlay's own state.
    let waveSpeed: number | undefined;
    if (typeof pipe.wallThickness === 'number' && pipe.wallThickness > 0) {
      const means = pipeMeanAcousticProps(pipe);
      const cK = kortewegWaveSpeed(means.c, means.rho, pipe.diameter, pipe.wallThickness);
      if (isFinite(cK) && cK > 0) waveSpeed = cK;
    }

    const st = createAcousticState(
      pipe,
      {
        [end]: { kind: 'closed', closeTime: 0 },
        [end === 'right' ? 'left' : 'right']: { kind: 'reservoir' },
      } as { left?: AcousticBoundarySpec; right?: AcousticBoundarySpec },
      // W7 caveat 5 — physical decay: linearized steady friction (default
      // Darcy f = 0.02) referenced to the pre-closure base velocity. First
      // Joukowsky peak is unchanged (≤2% per acceptance test D); the standing
      // wave now decays across 4L/c periods instead of ringing indefinitely.
      { friction: true, waveSpeed },
    );
    overlays.set(pipe.id, st);

    // W7 caveat 2 — column-separation check at arm time. The reservoir
    // reflection drives the closure face to P0 − ρ·c·Δu (frictionless bound,
    // with the overlay's OWN frozen ρ·c — i.e. the Korteweg-corrected
    // impedance when applied). Below p_sat(T_local) the liquid would flash;
    // the linear overlay cannot model that, so flag results as invalid past
    // the first downsurge. p_sat comes from the existing IAPWS adapter;
    // outside the saturation line (supercritical etc.) there is no p_sat to
    // compare against — those are not liquid lines and the two-phase warning
    // in createAcousticState already covers them.
    const bc = end === 'left' ? st.left : st.right;
    const du = Math.abs(bc.uTarget - bc.uBase);
    const faceCell = end === 'left' ? pipe.cells[0] : pipe.cells[pipe.nCells - 1];
    const pMin = faceCell.P - st.rho * st.c * du;
    const pSat = vaporPressureFromT_SI(faceCell.T);
    if (pSat !== null && pMin < pSat) {
      warnings?.push(columnSeparationWarning(pipe.id, pMin, pSat));
    }
  } catch (e: any) {
    warnings?.push(`Water-hammer overlay not armed for pipe ${pipe.id}: ${e?.message ?? e}`);
  }
}

/**
 * Per-frame acoustic event scan (exported for tests; production call site is
 * runNSStep, BEFORE the substep loop so closure overlays sample the
 * pre-closure base flow). Warnings land in the standard warnings array.
 */
export function detectValveClosures(state: NSSolverState, warnings?: string[]): void {
  if (!state.config.enableAcoustic) return;
  let prev = lastOpening.get(state);
  if (!prev) { prev = new Map(); lastOpening.set(state, prev); }
  const overlays = getAcousticOverlays(state);
  for (const comp of state.components.values()) {
    if (comp.type !== 'valve') continue;
    const open = Number(comp.params.opening ?? 1);
    const was = prev.get(comp.id) ?? open;
    prev.set(comp.id, open);

    // W7 caveat 5 — reopen lifecycle: opening rising past the threshold drops
    // this valve's feed-pipe overlays. Restore the base FV state FIRST so the
    // last-applied perturbation is not left baked into the cells.
    if (was <= VALVE_REOPEN_THRESHOLD && open > VALVE_REOPEN_THRESHOLD) {
      for (const { pipe } of feedPipesOf(state, comp.id)) {
        const st = overlays.get(pipe.id);
        if (!st) continue;
        removeAcousticPerturbations(st, pipe);
        overlays.delete(pipe.id);
      }
      continue;
    }

    // Rapid full closure only (v1) — thresholds unchanged from the W7 wiring.
    if (!(was > VALVE_REOPEN_THRESHOLD && open <= VALVE_CLOSED_THRESHOLD)) continue;
    for (const { pipe, end } of feedPipesOf(state, comp.id)) {
      if (pipe.nCells < 2) continue;
      armClosureOverlay(state, pipe, end, warnings);
    }
  }
}
// ─────────────────────────────────────────────────────────────────────────────

// ── R2-B4 transient event-schedule seam ─────────────────────────────────────
// Productionizes the orphaned coolingScenarios event vocabulary (applyEvents /
// applyLoadRamps) into a single pure-ish apply step. Called by runNSStep BEFORE
// detectValveClosures + the substep loop, so a scheduled valve slam (opening→0)
// mutates the live component param HERE, and detectValveClosures then samples
// that drop against the still-intact pre-closure base flow to arm the W7
// acoustic overlay (the "W7 arming order"). Default (no events) path is
// byte-identical: the guard returns immediately when events is empty/undefined.
/**
 * Apply scheduled transient events for the solver-step window [tPrev, tNow).
 *
 * DISCRETE events (`value` set) fire EXACTLY ONCE — when the event instant `t`
 * falls in the half-open window [tPrev, tNow). Successive step windows tile the
 * timeline with no overlap, so each discrete event fires in exactly one step.
 * (Front-inclusive so an event scheduled at t=0 fires on the first step.)
 *
 * RAMP events (`ramp` set) linearly interpolate the param `from → to` over
 * [t, t+duration], evaluated at the current step time `tNow`: held at `from`
 * before the start (param left untouched until then) and clamped at `to`
 * after the end. Applied every step while (and after) active.
 *
 * Mutates `state.components.get(targetNodeId).params[param]`. A target that is
 * not present in the current network is skipped silently (a canvas may not
 * contain every preset's target).
 */
export function applySimEvents(
  state: NSSolverState,
  events: SimEvent[] | undefined,
  tPrev: number,
  tNow: number,
): void {
  if (!events || events.length === 0) return; // byte-identical default path
  for (const e of events) {
    const comp = state.components.get(e.targetNodeId);
    if (!comp) continue; // target not in this network — skip (no throw)

    if (e.ramp) {
      // Continuous ramp — evaluate at tNow so the endpoint `to` is reliably
      // reached once the sim passes t+duration.
      const { from, to, duration } = e.ramp;
      const start = e.t;
      const end = e.t + Math.max(0, duration);
      if (tNow < start) continue; // not started — leave the param as-is
      const v =
        tNow >= end || !(duration > 0)
          ? to
          : from + (to - from) * ((tNow - start) / duration);
      comp.params[e.param] = v;
    } else if (typeof e.value === 'number') {
      // Discrete step — fires once when its instant is in [tPrev, tNow).
      if (e.t >= tPrev && e.t < tNow) {
        comp.params[e.param] = e.value;
      }
    }
  }
}
// ─────────────────────────────────────────────────────────────────────────────

export interface NSStepResult {
  nodes: Node[];
  /**
   * D-F5: canvas edges with per-edge flow state ({ phase, massFlow, running })
   * written into edge.data by syncNSToEdges — the FlowEdge renderer consumes
   * these fields. Returned as NEW edge objects; the input array is untouched.
   */
  edges: Edge[];
  simState: Partial<SimulationState>;
  nsSolverState: NSSolverState;
}

// Map predefined cycle names to NS network factories
const BOP_FACTORIES: Record<string, () => NSSolverState> = {
  'Electric Vapour Compression (EVC)': () => createBop101NS(),
  'Steam Vapour Compression (SVC)': () => createBop102NS(),
  'Steam Absorption Chiller (SAC)': () => createBop103NS(),
  'Steam Jet Cooler (SJC)': () => createBop104NS(),
  'DC Cooling Network': () => createCoolingNetworkNS(),
};

/**
 * Detect if the current canvas is a predefined BoP model.
 * Checks node labels against known BoP component patterns.
 */
function detectBoPModel(nodes: Node[]): string | null {
  const labels = nodes.map(n => (n.data?.label || '').toLowerCase());

  // Check for SVC-specific components
  if (labels.some(l => l.includes('svc turbine') || l.includes('svc splitter'))) {
    return 'Steam Vapour Compression (SVC)';
  }
  // Check for SAC-specific components
  if (labels.some(l => l.includes('absorption chiller') || l.includes('sac'))) {
    return 'Steam Absorption Chiller (SAC)';
  }
  // Check for SJC-specific components
  if (labels.some(l => l.includes('steam jet') || l.includes('sjc'))) {
    return 'Steam Jet Cooler (SJC)';
  }
  // Check for DC cooling network pattern
  const hasPump = labels.some(l => l.includes('pump'));
  const hasRack = labels.some(l => l.includes('rack row') || l.includes('rack_'));
  const hasPrimaryPump = labels.some(l => l.includes('primary pump'));
  const hasPlateHx = labels.some(l => l.includes('plate hx'));
  const hasHeatReject = labels.some(l => l.includes('heat rejection') || l.includes('chiller'));
  if (hasRack && hasPrimaryPump && hasPlateHx) {
    return 'DC Cooling Network';
  }
  if (hasRack && hasPump && hasHeatReject && nodes.length >= 10) {
    return 'DC Cooling Network';
  }

  // Check for basic EVC pattern (reactor + turbines + condenser + pumps, no SVC/SAC/SJC)
  const hasReactor = labels.some(l => l.includes('steam generator') || l.includes('reactor'));
  const hasTurbine = labels.some(l => l.includes('hpt') || l.includes('lpt') || l.includes('turbine'));
  if (hasReactor && hasTurbine && hasPump && nodes.length >= 6) {
    return 'Electric Vapour Compression (EVC)';
  }

  return null;
}

/**
 * Run one NS solver time step.
 */
export function runNSStep(
  nodes: Node[],
  edges: Edge[],
  simState: SimulationState,
  site: Site | null,
  existingState: NSSolverState | null,
  dt_input: number,
): NSStepResult {
  const warnings: string[] = [];
  const errors: string[] = [];

  if (nodes.length < 2 || edges.length < 1) {
    return {
      nodes,
      edges,
      simState: {
        time: simState.time,
        warnings: ['Add at least 2 connected components to run the NS solver.'],
        errors: [],
        metrics: simState.metrics,
      },
      nsSolverState: existingState!,
    };
  }

  try {
    let state: NSSolverState;

    if (!existingState) {
      // Try to detect a predefined BoP model and use dedicated NS network
      const bopName = detectBoPModel(nodes);
      const bopFactory = bopName ? BOP_FACTORIES[bopName] : null;

      if (bopFactory) {
        try {
          state = bopFactory();
          warnings.push(`Using optimized NS network for ${bopName}`);
        } catch (e: any) {
          // Fallback to auto-generated network
          state = buildNSNetwork(nodes, edges, DEFAULT_SIMPLE_CONFIG, site);
        }
      } else {
        // User-built system — auto-generate NS network
        try {
          state = buildNSNetwork(nodes, edges, DEFAULT_SIMPLE_CONFIG, site);
        } catch (e: any) {
          if (e instanceof UnsupportedCustomComponentExecutionError) {
            return {
              nodes,
              edges,
              simState: {
                time: simState.time,
                isRunning: false,
                warnings: [],
                errors: [`[${e.code}] ${e.componentId}.${e.field}: ${e.message}`],
                metrics: simState.metrics,
              },
              nsSolverState: existingState!,
            };
          }
          if (e instanceof NSPipeInputAdmissionError) {
            return {
              nodes,
              edges,
              simState: {
                time: simState.time,
                isRunning: false,
                warnings: [],
                errors: [`[${e.code}] ${e.message}`],
                metrics: simState.metrics,
              },
              nsSolverState: existingState!,
            };
          }
          return {
            nodes,
            edges,
            simState: { time: simState.time, warnings: [`Network: ${e.message}`], errors: [], metrics: simState.metrics },
            nsSolverState: existingState!,
          };
        }
      }
    } else {
      state = existingState;
    }

    // Thread the user's SIMULATION SETTINGS (relaxation / convergence tolerance)
    // into the solver config. This is applied EVERY step — both on the freshly
    // built/factory state and on a reused existing state — so changing the knobs
    // mid-run takes effect, and so the persisted state's config tracks the UI.
    //
    // deriveSolverConfig layers the user knobs over the network's BASE config
    // (the factory/builder-chosen triple) with sane clamps; at default knob
    // values it is a no-op, preserving the existing per-network relaxation
    // profile (0.3/0.7/0.8 auto-built, 0.05/0.1/0.3 BoP) and 1e-4 tolerance.
    // It does NOT touch the SIMPLE loop math or convergence criteria.
    //
    // The base for derivation is the config the network was originally built
    // with. We stash it once on first build so repeated derivation never
    // compounds the scale across steps.
    const baseConfig = (state as any)._baseConfig ?? { ...state.config };
    (state as any)._baseConfig = baseConfig;
    state.config = deriveSolverConfig(baseConfig, {
      relaxationFactor: simState.relaxationFactor,
      convergenceTolerance: simState.convergenceTolerance,
      // W7 P3 — user toggle for the MOC water-hammer overlay (default OFF).
      enableAcoustic: (simState as { enableAcoustic?: boolean }).enableAcoustic,
    });

    if (state.pipes.size === 0) {
      return {
        nodes,
        edges,
        simState: { warnings: ['No pipe segments. Connect components to create flow paths.'], errors: [], metrics: simState.metrics },
        nsSolverState: state,
      };
    }

    // Sub-stepping
    //
    // dt_max is the CFL-limited stable step (CFL number 5, hard floor 1e-3 s).
    // The per-substep dt must always satisfy dt <= dt_max for stability.
    //
    // autoTimeStep selects WHICH dt drives the substep size; it does NOT change
    // the CFL math (networkMaxTimeStep) or the substep loop:
    //   - false (fixed): dt = min(dt_input, dt_max, 0.1) — user dt clamped down
    //                    by CFL and the absolute 0.1 s safety cap. Unchanged.
    //   - true (adaptive): dt = min(dt_max, 0.1) — CFL drives the step, no longer
    //                    forced down to the fixed user dt. Still CFL-stable because
    //                    dt <= dt_max, and still bounded by the 0.1 s safety cap.
    const dt_max = Math.max(0.001, networkMaxTimeStep(state, 5));
    const dt = simState.autoTimeStep
      ? Math.min(dt_max, 0.1)
      : Math.min(dt_input, Math.min(dt_max, 0.1));
    const nSubsteps = Math.min(10, Math.max(1, Math.ceil(dt_input / dt)));

    // Detect cooling network for HX thermal coupling
    const isCoolingNetwork = state.components.has('pri_hx_0') && state.components.has('sec_hx_0');

    // R2-B4 — apply scheduled transient events for THIS step's time window
    // BEFORE detectValveClosures + the substep loop. simState.time is t_prev
    // and simState.time + dt_input is t_now (the reported time advance below).
    // A scheduled valve slam mutates the live opening param here so the very
    // next detectValveClosures call sees the drop and arms the W7 overlay on
    // the pre-closure base flow. No-op (byte-identical) when events is empty.
    applySimEvents(state, simState.events, simState.time, simState.time + dt_input);

    // W7 P2 — arm acoustic overlays for freshly-closed valves BEFORE the base
    // flow relaxes (the wave amplitude samples the pre-closure velocity).
    // R1-A3: warnings threaded so arm-time column-separation (cavitation)
    // checks surface through the standard warnings array.
    detectValveClosures(state, warnings);

    // N6: OR the per-step cap flag across ALL substeps — a cap engagement in
    // any substep must surface even if the final substep stayed single-phase.
    let twoPhaseCapEngaged = false;
    for (let sub = 0; sub < nSubsteps; sub++) {
      try {
        if (isCoolingNetwork) {
          updateHXCoupling(state, DEFAULT_COOLING_CONFIG);
        }
        // W7 P1 — overlay order per substep: strip the acoustic perturbation
        // (SIMPLE must never relax the wave away), advance the base flow, then
        // advance + re-apply the wave on the fresh base. The module runs its
        // own exact-CFL acoustic clock internally (dt_sub = dx/c).
        const ac = state.config.enableAcoustic ? acousticOverlays.get(state) : undefined;
        if (ac) for (const [id, s] of ac) { const p = state.pipes.get(id); if (p) removeAcousticPerturbations(s, p); }
        networkSimpleStep(state, dt);
        if (ac) for (const [id, s] of ac) { const p = state.pipes.get(id); if (p) acousticStep(s, p, dt); }
        if (state.twoPhaseFrictionCapEngaged) twoPhaseCapEngaged = true;
      } catch (e: any) {
        if (e instanceof NSPipeInputAdmissionError) {
          errors.push(`[${e.code}] ${e.message}`);
          break;
        }
        warnings.push(`NS step: ${e.message}`);
        break;
      }
    }

    // N6: surface the capped two-phase friction as a user-visible warning —
    // wet-line dP is under-predicted while the 50x stability cap is active.
    if (twoPhaseCapEngaged) {
      warnings.push(TWO_PHASE_CAP_WARNING);
    }

    if (!state.converged) {
      const res = state.residuals;
      if (isFinite(res.mass)) {
        warnings.push(`NS solver: mass residual ${res.mass.toExponential(2)}`);
      }
    }

    // First-law energy closure (read-only diagnostic). Surfaces the network
    // energy imbalance as a % on metrics.convergence and warns above the 0.1%
    // tolerance per the conservation-law rule. Does NOT affect the solve.
    const energyClosure = computeEnergyClosure(state);
    if (energyClosure.warning) warnings.push(energyClosure.warning);

    const updatedNodes = syncNSToNodes(nodes, state);
    // D-F5: write per-edge { phase, massFlow, running } for the FlowEdge
    // renderer. `running` reflects the live run loop driving this step.
    const updatedEdges = syncNSToEdges(edges, state, simState.isRunning === true);

    return {
      nodes: updatedNodes,
      edges: updatedEdges,
      simState: {
        time: simState.time + dt_input,
        timeStep: dt,
        ...(errors.length > 0 ? { isRunning: false } : {}),
        warnings,
        errors,
        metrics: {
          ...simState.metrics,
          convergence: {
            // Non-finite residual = diverged. Report POSITIVE_INFINITY (not 0)
            // so the high-residual / "Solver diverging" UI fires instead of a
            // perpetual amber "Solving…".
            residual: isFinite(state.residuals.mass) ? state.residuals.mass : Number.POSITIVE_INFINITY,
            iterations: state.iteration,
            isConverged: state.converged,
            energyClosurePct: energyClosure.closurePct ?? undefined,
          },
        },
      },
      nsSolverState: state,
    };
  } catch (e: any) {
    return {
      nodes,
      edges,
      simState: { time: simState.time, warnings: [`NS error: ${e.message}`], errors: [], metrics: simState.metrics },
      nsSolverState: existingState!,
    };
  }
}
